import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Eye, Package, Printer, ShoppingCart, Search, X, Image, Video, Link as LinkIcon, Play } from 'lucide-react';
import { FileUploader } from './FileUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminFarms = ({ farms, onRefresh }) => {
  const { token } = useAuth();
  const { t, i18n } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFarm, setEditingFarm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contentLang, setContentLang] = useState('zh');
  const [formData, setFormData] = useState({
    name: '', name_en: '', name_ru: '',
    location: '', location_en: '', location_ru: '',
    altitude: '',
    description: '', description_en: '', description_ru: '',
    story: '', story_en: '', story_ru: '',
    image_url: '', images: [], video_url: '',
    products: [], certifications: [], founded_year: '',
    is_daily_pick: false, is_active: true, sort_order: 0
  });
  const [productInput, setProductInput] = useState('');

  const productOptions = ['🥬 蔬菜', '🍎 水果', '🌾 谷物', '🥜 坚果', '🫘 豆类', '🍵 茶叶', '🌿 草药'];
  const certOptions = ['有机认证', '绿色食品', '无公害', 'GAP认证'];

  const resetForm = () => {
    setFormData({
      name: '', name_en: '', name_ru: '',
      location: '', location_en: '', location_ru: '',
      altitude: '',
      description: '', description_en: '', description_ru: '',
      story: '', story_en: '', story_ru: '',
      image_url: '', images: [], video_url: '',
      products: [], certifications: [], founded_year: '',
      is_daily_pick: false, is_active: true, sort_order: 0
    });
    setEditingFarm(null);
    setContentLang('zh');
  };

  const openCreateDialog = () => { resetForm(); setDialogOpen(true); };

  const openEditDialog = (farm) => {
    setEditingFarm(farm);
    setFormData({
      name: farm.name || '', name_en: farm.name_en || '', name_ru: farm.name_ru || '',
      location: farm.location || '', location_en: farm.location_en || '', location_ru: farm.location_ru || '',
      altitude: farm.altitude || '',
      description: farm.description || '', description_en: farm.description_en || '', description_ru: farm.description_ru || '',
      story: farm.story || '', story_en: farm.story_en || '', story_ru: farm.story_ru || '',
      image_url: farm.image_url || '', images: farm.images || [], video_url: farm.video_url || '',
      products: farm.products || [], certifications: farm.certifications || [], founded_year: farm.founded_year || '',
      is_daily_pick: farm.is_daily_pick || false, is_active: farm.is_active !== false, sort_order: farm.sort_order || 0
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.image_url) {
      toast.error(t('admin.requiredFields', '请填写必填字段'));
      return;
    }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingFarm) {
        await axios.put(`${API}/farms/${editingFarm.id}`, formData, { headers });
        toast.success(t('admin.farmUpdated', '农场已更新'));
      } else {
        await axios.post(`${API}/farms`, formData, { headers });
        toast.success(t('admin.farmCreated', '农场已创建'));
      }
      setDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.confirmDeleteFarm', '确定删除此农场吗？'))) return;
    try {
      await axios.delete(`${API}/farms/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('admin.farmDeleted', '农场已删除'));
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  const toggleProduct = (product) => {
    if (formData.products.includes(product)) {
      setFormData({ ...formData, products: formData.products.filter(p => p !== product) });
    } else {
      setFormData({ ...formData, products: [...formData.products, product] });
    }
  };

  const toggleCert = (cert) => {
    if (formData.certifications.includes(cert)) {
      setFormData({ ...formData, certifications: formData.certifications.filter(c => c !== cert) });
    } else {
      setFormData({ ...formData, certifications: [...formData.certifications, cert] });
    }
  };

  const getField = (base) => contentLang === 'en' ? `${base}_en` : contentLang === 'ru' ? `${base}_ru` : base;

  return (
    <div data-testid="admin-farms">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('admin.farms', '农场管理')}</h1>
        <Button onClick={openCreateDialog} className="bg-green-600 hover:bg-green-700" data-testid="create-farm-btn">
          <Plus className="w-4 h-4 mr-2" /> {t('admin.addFarm', '添加农场')}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {farms.length === 0 ? (
          <Card className="p-8 text-center text-gray-500 col-span-full">{t('admin.noFarms', '暂无农场')}</Card>
        ) : (
          farms.map((farm) => (
            <Card key={farm.id} className="overflow-hidden">
              <div className="aspect-video bg-gray-100 relative">
                <img src={farm.image_url} alt={farm.name} className="w-full h-full object-cover" />
                {farm.is_daily_pick && (
                  <Badge className="absolute top-2 left-2 bg-orange-500">⭐ {t('admin.dailyPick', '每日推荐')}</Badge>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-lg">{farm.name}</h3>
                <p className="text-sm text-gray-500 mb-2">📍 {farm.location}</p>
                {farm.altitude && <p className="text-xs text-gray-400 mb-2">海拔 {farm.altitude}</p>}
                <div className="flex flex-wrap gap-1 mb-3">
                  {farm.products?.slice(0, 3).map((p, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{p}</Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(farm)}>
                    <Edit className="w-4 h-4 mr-1" /> {t('common.edit')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(farm.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFarm ? t('admin.editFarm', '编辑农场') : t('admin.addFarm', '添加农场')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Language tabs */}
            <div className="flex gap-2 mb-4">
              {['zh', 'en', 'ru'].map((lang) => (
                <Button key={lang} variant={contentLang === lang ? 'default' : 'outline'} size="sm" onClick={() => setContentLang(lang)}>
                  {lang === 'zh' ? '中文' : lang === 'en' ? 'English' : 'Русский'}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('admin.farmName', '农场名称')} *</Label>
                <Input value={formData[getField('name')]} onChange={(e) => setFormData({ ...formData, [getField('name')]: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.farmLocation', '位置')}</Label>
                <Input value={formData[getField('location')]} onChange={(e) => setFormData({ ...formData, [getField('location')]: e.target.value })} placeholder="云南省大理市" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('admin.altitude', '海拔')}</Label>
                <Input value={formData.altitude} onChange={(e) => setFormData({ ...formData, altitude: e.target.value })} placeholder="2000米" />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.foundedYear', '成立年份')}</Label>
                <Input value={formData.founded_year} onChange={(e) => setFormData({ ...formData, founded_year: e.target.value })} placeholder="2005" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('admin.farmDesc', '简介')}</Label>
              <Textarea value={formData[getField('description')]} onChange={(e) => setFormData({ ...formData, [getField('description')]: e.target.value })} rows={2} />
            </div>

            <div className="space-y-2">
              <Label>{t('admin.farmStory', '农场故事')}</Label>
              <Textarea value={formData[getField('story')]} onChange={(e) => setFormData({ ...formData, [getField('story')]: e.target.value })} rows={3} />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>{t('admin.mainImage', '主图')} *</Label>
              <div className="flex gap-2">
                <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://..." className="flex-1" />
                <FileUploader type="image" accept="image/*" onUpload={(url) => setFormData({ ...formData, image_url: url })} label={t('admin.upload')} />
              </div>
              {formData.image_url && <img src={formData.image_url} alt="Preview" className="w-32 h-20 object-cover rounded mt-2" />}
            </div>

            <div className="space-y-2">
              <Label>{t('admin.products', '主营产品')}</Label>
              <div className="flex flex-wrap gap-2">
                {productOptions.map((p) => (
                  <Badge 
                    key={p} 
                    variant={formData.products.includes(p) ? 'default' : 'outline'} 
                    className="cursor-pointer"
                    onClick={() => toggleProduct(p)}
                  >
                    {p}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('admin.certifications', '认证')}</Label>
              <div className="flex flex-wrap gap-2">
                {certOptions.map((c) => (
                  <Badge 
                    key={c} 
                    variant={formData.certifications.includes(c) ? 'default' : 'outline'} 
                    className="cursor-pointer"
                    onClick={() => toggleCert(c)}
                  >
                    {c}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator />

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="farm_is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="rounded" />
                <Label htmlFor="farm_is_active">{t('admin.show', '显示')}</Label>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="farm_is_daily_pick" 
                  checked={formData.is_daily_pick} 
                  onChange={(e) => setFormData({ ...formData, is_daily_pick: e.target.checked })} 
                  className="rounded border-orange-300 text-orange-500" 
                />
                <Label htmlFor="farm_is_daily_pick" className="text-orange-600 font-medium">⭐ {t('admin.dailyPick', '每日推荐')}</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};



export default AdminFarms;
